<template>
  <div class="country-selector">
    <md-progress-bar
      v-if="loading"
      md-mode="indeterminate"
    />
    <div
      v-else
      class="node-form__tags tags"
    >
      <div class="country-picker-wrapper">
        <md-autocomplete
          ref="input"
          v-model="inputValue"
          class="tags__input"
          :md-options="isSearchMode ? searchOptions : items"
          :md-fuzzy-search="false"
          @md-changed="onInputChange"
        >
          <label v-if="label">{{ label }}</label>

          <template
            slot="md-autocomplete-item"
            slot-scope="{ item, term }"
          >
            <div
              class="selector-option"
              :class="isParent(item) ? `region-option` : 'country-option'"
            >
              <md-checkbox
                :value="!isChecked(item)"
                class="region-checkbox"
                @change="onItemClick(item)"
              />
              <md-highlight-text :md-term="term">
                {{ item }}
              </md-highlight-text>
              <md-button
                v-if="isParent(item)"
                class="md-icon-button"
                @click.stop="toggleGroupList(item)"
              >
                <md-icon>
                  {{ unfoldedGroups.includes(item) ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                </md-icon>
              </md-button>
            </div>
          </template>
        </md-autocomplete>
      </div>
    </div>

    <div
      v-if="showSelectedBubbles"
      class="selected-output"
    >
      <md-chip
        v-for="group in selectedGroups"
        :key="group"
        md-deletable
        class="md-primary"
        @md-delete="deselectGroup(group)"
      >
        {{ group }}
      </md-chip>
      <md-chip
        v-for="item in itemsFromUnselectedGroups"
        :key="item"
        md-deletable
        class="md-accent"
        @md-delete="deselectItem(item)"
      >
        {{ item }}
      </md-chip>
    </div>
  </div>
</template>

<script>
const LIST_UPDATED = "list-updated";

export default {
    name: "GroupedAutoComplete",
    props: {
        groups: { type: Object, required: true },
        selected: { type: Array, default: () => [] },
        loading: Boolean,
        label: { type: String, default: undefined },
        showSelectedBubbles: Boolean,

        searchOptions: { type: Array, default: undefined },
    },
    data() {
        return {
            items: [],
            inputValue: "",
            selectedItems: [],
            selectedGroups: [],
            unfoldedGroups: [],
        };
    },
    computed: {
        itemsFromUnselectedGroups() {
            return Object.entries(this.$props.groups)
                .filter(([group]) => !this.selectedGroups.includes(group))
                .flatMap(([, items]) => items)
                .filter(item => this.selectedItems.includes(item));
        },
        isSearchMode() {
            return !!this.inputValue;
        }
    },
    watch: {
        selected() {
            this.selectedItems = [...this.$props.selected];
            // if there is a group, all items in which are selected, also add the group to the list of selected groups,
            // unless it's already there
            Object.entries(this.$props.groups).forEach(([groupName, items]) => {
                if (items.every(i => this.selectedItems.includes(i)) && !this.selectedGroups.includes(groupName)) {
                    this.selectedGroups.push(groupName);
                }
            });
        },
        groups() {
            if (this.items.length === 0) {
                this.items = Object.keys(this.$props.groups);
            }
        }
    },
    mounted() {
        this.formInitialItems();
    },
    methods: {
        formInitialItems() {
            // initial items will be only folded group headers
            this.items = Object.keys(this.$props.groups);
            // selecting checked items
            this.selectedItems = this.$props.selected ? [...this.$props.selected] : [];
            // selecting groups, where all children are selected
            Object.entries(this.$props.groups).forEach(([groupName, items]) => {
                items.reduce(
                    (acc, cur) => this.selectedItems.includes(cur) && acc,
                    true
                ) && this.selectedGroups.push(groupName);
            });
        },
        isParent(item) {
            return Object.keys(this.$props.groups).includes(item);
        },
        emitUpdate() {
            this.$emit(LIST_UPDATED, this.selectedItems);
        },
        selectItem(item) {
            this.selectedItems.push(item);
        },
        selectGroup(item) {
            this.selectedGroups.push(item);
            this.$props.groups[item].forEach(i => {
                if (!this.selectedItems.includes(i)) {
                    this.selectedItems.push(i);
                }
            });
        },
        deselectItem(item) {
            this.selectedItems = this.selectedItems.filter(i => i !== item);

            // find group to which `item` belongs
            const [groupName] = Object.entries(this.$props.groups).find(([,children]) => children.includes(item));

            // deselecting group if it's selected
            if (this.selectedGroups.includes(groupName)) {
                this.selectedGroups = this.selectedGroups.filter(e => e !== groupName);
            }
        },
        deselectGroup(item) {
            this.selectedGroups = this.selectedGroups.filter(i => i !== item);
            this.selectedItems = this.selectedItems.filter(i => !this.$props.groups[item].includes(i));
        },
        onItemClick(item) {
            if (this.isParent(item)) {
                this.selectedGroups.includes(item)
                    ? this.deselectGroup(item)
                    : this.selectGroup(item);
            } else {
                this.selectedItems.includes(item)
                    ? this.deselectItem(item)
                    : this.selectItem(item);
            }
            this.emitUpdate();
            this.clearInput();
        },
        isChecked(item) {
            return this.isParent(item) ? this.selectedGroups.includes(item) : this.selectedItems.includes(item);
        },
        unfoldGroup(groupName) {
            this.unfoldedGroups.push(groupName);
            this.items.splice(
                this.items.findIndex(e => e === groupName) + 1,
                0,
                ...this.$props.groups[groupName]
            );
        },
        foldGroup(groupName) {
            this.unfoldedGroups = this.unfoldedGroups.filter(i => i !== groupName);
            this.items.splice(
                this.items.findIndex(e => e === groupName) + 1,
                this.$props.groups[groupName].length
            );
        },
        toggleGroupList(item) {
            this.unfoldedGroups.includes(item)
                ? this.foldGroup(item)
                : this.unfoldGroup(item);
        },
        onInputChange() {
            Object.keys(this.$props.groups).filter(r => !this.unfoldedGroups.includes(r))
                .forEach(r => this.unfoldGroup(r));
        },
        clearInput() {
            this.inputValue = "";
            this.$refs.input.$data.searchTerm = "";
        },
    }
};
</script>
<style lang="scss">
.country-picker-wrapper {
  display: flex;
  align-items: center;

  .add-county-button {
    height: 35px !important;
  }
}

.md-list-item-container.md-button-clean {
  .md-list-item-content.md-ripple {
    padding: 0 !important;
  }
}

.selector-option {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.region-option {
  margin: 0 5px;
  padding-right: 5px;
  background-color: #43a047;
  color: white;
  .md-checkbox.md-theme-default {
    margin: 0 5px 0 10px !important;
    background-color: white;
    .md-checkbox-container {
      border-color: white;
    }
  }
}

.country-option {
  .md-checkbox.md-theme-default {
    margin: 0 5px 0 15px !important;
  }
  .md-highlight-text {
    font-size: 12px;
  }
}

.md-menu-content-bottom-start.md-menu-content-small.md-menu-content.md-theme-default {
  width: 300px;
  min-width: 300px;
}
</style>
